import { Alert, Snackbar } from "@mui/material";
import type { SnackbarCloseReason } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import type { ReactNode, SyntheticEvent } from "react";
import { NotificationContext } from "./notification.context";
import { Notification, NotificationContextValue, NotificationSeverity } from "./notification.type";

interface NotificationProviderProps {
    /** Component tree that can call `useNotification`. */
    children: ReactNode;
}

/**
 * Renders one toast at a time for notifications raised by its descendants.
 * A newer notification replaces the visible one instead of waiting in a queue.
 *
 * @param props - The provider props.
 * @returns The provider with the toast surface mounted next to its children.
 */
function NotificationProvider({ children }: NotificationProviderProps) {
    const [notification, setNotification] = useState<Notification | null>(null);
    const [open, setOpen] = useState(false);

    const notify = useCallback((message: string, severity: NotificationSeverity = "info") => {
        setNotification({ id: Date.now(), message, severity });
        setOpen(true);
    }, []);

    const handleSnackbarClose = useCallback((_event: SyntheticEvent | Event, reason: SnackbarCloseReason) => {
        if (reason === "clickaway") {
            return;
        }

        setOpen(false);
    }, []);

    const handleAlertClose = useCallback(() => {
        setOpen(false);
    }, []);

    const value = useMemo<NotificationContextValue>(() => ({ notify }), [notify]);

    return (
        <NotificationContext value={value}>
            {children}
            <Snackbar
                key={notification?.id}
                open={open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={notification?.severity ?? "info"} variant="filled" onClose={handleAlertClose}>
                    {notification?.message}
                </Alert>
            </Snackbar>
        </NotificationContext>
    );
}

export default NotificationProvider;
